import type { Event, EventHint } from "@sentry/types";

/**
 * Filters out common non-actionable errors:
 * - Browser extensions (Grammarly, AdBlock, Bitwarden, etc.)
 * - Ad/tracking blockers (Brave Shields, uBlock, Ghostery)
 * - Analytics/tracking scripts (GTM, Hotjar, Facebook Pixel)
 * - Known browser noise (ResizeObserver, blocked scripts)
 */
export function filterNoiseEvents(
    event: Event,
    hint?: EventHint,
): Event | null {
    const error = hint?.originalException;
    const message = (error as Error)?.message || "";
    const stack = (error as Error)?.stack || "";
    const exceptionValue = event?.exception?.values?.[0]?.value || "";
    const requestUrl = event?.request?.url || "";

    // 1️⃣ Ignore browser extension stack frames
    const isExtensionFrame =
        event.exception?.values?.some((ex) =>
            ex.stacktrace?.frames?.some((frame) =>
                /chrome-extension:|moz-extension:|safari-extension:|edge-extension:/i.test(
                    frame.filename || "",
                ),
            ),
        ) ?? false;
    if (isExtensionFrame) return null;

    // 2️⃣ Ignore known extension-related errors
    if (
        /(Extension|Grammarly|AdBlock|uBlock|Ghostery|LastPass|Bitwarden|Honey|Loom)/i.test(
            exceptionValue,
        )
    ) {
        return null;
    }

    // 3️⃣ Ignore tracker or ad blocker network errors
    if (
        /(ERR_BLOCKED_BY_CLIENT|ERR_BLOCKED_BY_RESPONSE|Blocked a frame with origin|Refused to load the script)/i.test(
            message,
        )
    ) {
        return null;
    }

    // 4️⃣ Ignore analytics / tracker URLs
    if (
        /(chrome-extension|moz-extension|safari-extension|google-analytics|googletagmanager|facebook\.net|hotjar|clarity|segment|mixpanel|doubleclick|adsystem)/i.test(
            stack + requestUrl,
        )
    ) {
        return null;
    }

    // 5️⃣ Ignore common noisy browser errors
    if (
        /(ResizeObserver loop limit exceeded|Script error\.|Network Error|Non-Error promise rejection captured|Failed to execute \'define\' on \'CustomElementRegistry\': the name \"top-bar\" has already been used with this registry|Shopify root is null|Can\'t find variable|null is not an object \(evaluating \'document.elementFromPoi)/i.test(
            message,
        )
    ) {
        return null;
    }

    return event;
}
